import type { FC } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { setFont, colorsPalettes, colorsSets } from "@similarweb/styles";
import { PlainTooltip } from "@similarweb/ui-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { i18nFilter } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { DefaultFetchService } from "app/services/fetchService";

const Text = styled.label<{ marginRight?: string }>`
    display: inline-block;
    margin-right: 6px;
    overflow: hidden;
    margin-top: auto;
    margin-bottom: auto;
    white-space: nowrap;
    cursor: inherit;
    width: fit-content;
    ${({ marginRight }) => marginRight && `margin-right:${marginRight};`};
    ${setFont({ $size: 16, $color: colorsPalettes.carbon[300] })}
`;

const FlexBox = styled.div`
    margin-top: 5px;
    display: flex;
    justify-content: space-between;
`;

const InfoIconContainer = styled.div`
    margin-top: 1px;
`;

const Circle = styled.span`
    border-radius: 50%;
    text-align: center;
    margin-top: inherit;
    margin-right: 5px;
    width: 10px;
    height: 10px;
    ${({ color }) => `background-color:${color};`};
`;

const OrganicSearchZeroClicksContainer = styled.div<{ visibilityValue: string }>`
    ${({ visibilityValue }) => `visibility:${visibilityValue};`};
`;

const InfoTooltip: FC<{ infoText: string }> = ({ infoText }) => {
    return (
        <PlainTooltip tooltipContent={infoText}>
            <InfoIconContainer>
                <SWReactIcons
                    iconName="info"
                    size="xs"
                />
            </InfoIconContainer>
        </PlainTooltip>
    );
};

interface IOrganicSearchZeroClicksProps {
    text: string;
    value: string;
    toolTipInfoText?: string;
    bulletColor?: string;
}

const OrganicSearchZeroClicksInner: FC<IOrganicSearchZeroClicksProps> = ({
    text,
    value,
    toolTipInfoText,
    bulletColor,
}) => {
    return (
        <FlexBox>
            <FlexBox>
                {bulletColor ? <Circle color={bulletColor} /> : null}
                <Text data-automation-text>{text}</Text>
                {toolTipInfoText ? <InfoTooltip infoText={toolTipInfoText} /> : null}
            </FlexBox>
            <Text data-automation-value>{value}</Text>
        </FlexBox>
    );
};

const isGroupContext = (keys) => typeof keys !== "undefined" && keys.substring(0, 1) === "*";

const getZeroClickRatio = async (queryParams) => {
    const apiParams = queryParams;
    const fetchService = DefaultFetchService.getInstance();
    // if it's a group context
    if (isGroupContext(queryParams.keys)) {
        const groupId = queryParams.keys.substring(1);
        const userGroups = await keywordGroupsQueryClient.getUserGroups();
        const groupHash = userGroups.find((group) => group.Id === groupId)?.GroupHash;
        apiParams.groupHash = groupHash;
    } else {
        delete apiParams.groupHash;
    }
    return fetchService.get(`widgetApi/KeywordAnalysisOP/KeywordAnalysisGroupOrganic/SingleMetric`, apiParams);
};

const getZeroClicksValue = (val) => (val === 0 || val === 100 ? "N/A" : val + "%");

const ShowOnlyZeroClicks = ({ zeroClickRatio }) => {
    const i18n = i18nFilter();
    const zeroClickTooltip = i18n("keyword.analysis.organic.search.volume.zero.click.tooltip.info");
    const zeroClickText = i18n("keyword.analysis.organic.search.volume.did.not.click");
    const percentageFormatter = percentageFilter();
    const zeroClicksValue = percentageFormatter(zeroClickRatio, 0);
    const zeroClickPercentage = parseInt(zeroClicksValue, 10);
    return (
        <OrganicSearchZeroClicksContainer
            className={"ZeroClicksContainer"}
            visibilityValue={zeroClickRatio === undefined ? "hidden" : "visible"}
        >
            <OrganicSearchZeroClicksInner
                toolTipInfoText={zeroClickTooltip}
                text={zeroClickText}
                value={getZeroClicksValue(zeroClickPercentage)}
            />
        </OrganicSearchZeroClicksContainer>
    );
};

const OrganicSearchZeroClicks = ({ zeroClickRatio, showBullets = true }) => {
    const i18n = i18nFilter();
    const percentageFormatter = percentageFilter();
    const zeroClicksValue = percentageFormatter(zeroClickRatio, 0);
    const nonZeroClickText = i18n("keyword.analysis.organic.search.volume.did.click");
    const zeroClickText = i18n("keyword.analysis.organic.search.volume.did.not.click");
    const zeroClickPercentage = parseInt(zeroClicksValue, 10);

    const organicSearchItems = (
        <OrganicSearchZeroClicksContainer
            className={"ZeroClicksContainer"}
            visibilityValue={zeroClickRatio === undefined ? "hidden" : "visible"}
        >
            <OrganicSearchZeroClicksInner
                bulletColor={showBullets ? colorsSets.c["others"] : null}
                text={zeroClickText}
                value={getZeroClicksValue(zeroClickPercentage)}
            />
            <OrganicSearchZeroClicksInner
                bulletColor={showBullets ? colorsPalettes.blue[500] : null}
                text={nonZeroClickText}
                value={getZeroClicksValue(100 - zeroClickPercentage)}
            />
        </OrganicSearchZeroClicksContainer>
    );
    return organicSearchItems;
};
const OrganicSearchZeroClicksPlaceholder = styled.div`
    height: 60px;
`;
export const OrganicDataRows = ({ items }) => {
    const rows = items.map((item, index) => (
        <OrganicSearchZeroClicksInner
            key={index}
            text={item.text}
            value={item.value}
            bulletColor={item.color}
        />
    ));
    return <>{rows}</>;
};
